import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { Globe, Plus } from "lucide-react";

import { AlertNote } from "@/components/AlertNote";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { extractHost, splitLines } from "../seoSetupUtils";
import { NumberedTextarea, RemoveButton, ServerErrors, SetupCard } from "../components";

export function CompetitorsStep({ competitors, clientHost, onChange, max, errors, showErrors, serverErrors }) {
  // Starts with the competitors already added, so coming back to the step keeps the list in sight.
  const [text, setText] = useState(() => competitors.join("\n"));

  const preview = useMemo(() => {
    const invalid = [];
    const domains = [];
    let own = false;
    splitLines(text).forEach((line) => {
      const host = extractHost(line);
      if (!host) invalid.push(line);
      else if (clientHost && host === clientHost) own = true;
      else if (!competitors.includes(host) && !domains.includes(host)) domains.push(host);
    });
    return { domains, invalid, own, overflow: competitors.length + domains.length > max };
  }, [text, competitors, clientHost, max]);

  const canAdd = preview.domains.length > 0 && !preview.overflow && !preview.invalid.length;

  const handleAdd = () => {
    if (!canAdd) return;
    // The text stays in the box; what is already added no longer counts as new.
    onChange((prev) => ({ competitors: [...prev.competitors, ...preview.domains] }));
  };

  const { invalid } = preview;

  return (
    <Stack spacing={2}>
      <ServerErrors messages={serverErrors} />

      <SetupCard
        title="Añadir competidores"
        description="Webs con las que comparar al cliente, un dominio por línea. Es opcional: puedes continuar sin añadir ninguna."
        aside={
          <Badge color={competitors.length >= max ? "pending" : "muted"} dot={false}>
            {competitors.length}/{max}
          </Badge>
        }
        footer={
          <>
            <Typography variant="caption" color="text.secondary">
              {preview.own ? "El dominio del propio cliente no se añade como competidor." : " "}
            </Typography>
            <Button variant="dark" onClick={handleAdd} disabled={!canAdd} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Plus size={16} />
              Añadir competidores{preview.domains.length ? ` (${preview.domains.length})` : ""}
            </Button>
          </>
        }
      >
        <NumberedTextarea
          id="seo-setup-competitors"
          value={text}
          onChange={setText}
          rows={4}
          placeholder={"competidor1.es\ncompetidor2.com"}
          ariaLabel="Competidores, un dominio por línea"
          error={invalid.length > 0 || preview.overflow}
        />
        {invalid.length > 0 && (
          <AlertNote variant="warning" sx={{ mt: 1.5 }}>
            {invalid.length === 1
              ? `«${invalid[0]}» no es un dominio válido.`
              : `${invalid.length} líneas no son dominios válidos: ${invalid.slice(0, 3).join(", ")}${invalid.length > 3 ? "…" : ""}`}
          </AlertNote>
        )}
        {preview.overflow && (
          <AlertNote variant="warning" sx={{ mt: 1.5 }}>
            Como máximo puedes añadir {max} competidores; te quedan {Math.max(max - competitors.length, 0)}.
          </AlertNote>
        )}
      </SetupCard>

      {competitors.length > 0 && (
        <SetupCard title="Competidores añadidos al proyecto">
          <Box
            component="ul"
            sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}
          >
            {competitors.map((domain) => {
              const error = showErrors ? errors[domain] : null;
              return (
                <Box
                  component="li"
                  key={domain}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.5,
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: error ? "#FCA5A5" : "#E8ECF2",
                    backgroundColor: "#fff",
                    minWidth: 0,
                  }}
                >
                  <Globe size={14} color="#64748B" style={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {domain}
                    </Typography>
                    {error && (
                      <Typography variant="caption" color="error">
                        {error}
                      </Typography>
                    )}
                  </Box>
                  <RemoveButton
                    size={15}
                    label={`Quitar ${domain}`}
                    onClick={() => onChange((prev) => ({ competitors: prev.competitors.filter((d) => d !== domain) }))}
                  />
                </Box>
              );
            })}
          </Box>
        </SetupCard>
      )}
      {showErrors && errors.list && (
        <Typography variant="caption" color="error">
          {errors.list}
        </Typography>
      )}
    </Stack>
  );
}

CompetitorsStep.propTypes = {
  competitors: PropTypes.arrayOf(PropTypes.string).isRequired,
  clientHost: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.number.isRequired,
  errors: PropTypes.object.isRequired,
  showErrors: PropTypes.bool,
  serverErrors: PropTypes.arrayOf(PropTypes.string),
};
